import numeral from "numeral";
import {
    FEATURE_OUTBOUND_EMAILS,
    FEATURE_PROTECT_DOMAINS,
    PRICING_SCHEME_STAIRSTEP,
    PRICING_SCHEME_TIERED,
    EMAIL_RANGE_LIMIT,
    THOUSANDS_OF_MESSAGES,
} from "./constants";

//round to left (default 2 digits)
const roundToLeft = (num, left = 2) => {
    const digit = num.toString().length;
    if (digit <= left) return num;
    return Math.round(num / 10 ** (digit - left)) * 10 ** (digit - left);
};

/**
 * For outbound slider section, generate slider to email count mapping relations.
 * example sliderToEmail Object:
 *      key:        [0, 1, 2, 3, 4]
 *      sliders:    [0, 250, 500, 750, 1000]
 *      emailCount: [0, planLimit1, planLimit2, planLimit3, planLimit4]
 */
const sliderToEmailMap = outboundPlans => {
    let sliderToEmail = [];
    sliderToEmail.push({ key: 0, slider: 0, count: 0 });
    const intervalCount = outboundPlans.length;
    outboundPlans.forEach((outboundPlan, index) => {
        sliderToEmail.push({
            key: index + 1,
            slider: (1000 / intervalCount) * (index + 1),
            count: outboundPlan.includedEmails,
        });
    });
    return sliderToEmail;
};

const sliderEmailConverter = (sliderValue, plans) => {
    const sliderToEmails = sliderToEmailMap(plans);
    const nearestSlider = sliderToEmails.filter(item => item.slider <= sliderValue).pop();
    if (nearestSlider.slider === sliderValue) {
        return nearestSlider.count;
    }
    const nextPoint = nearestSlider.key + 1;
    return Math.round(
        nearestSlider.count +
            ((sliderToEmails[nextPoint]["count"] - nearestSlider["count"]) /
                (sliderToEmails[nextPoint]["slider"] - nearestSlider["slider"])) *
                (sliderValue - nearestSlider["slider"])
    );
};

const findIndex = (allocation, prices) => {
    //iterate through the prices intervals and try to find the index current allocation belongs to
    // e.g.      prices_index.lowerBound <= allocation <=  prices_index.upperBound
    let index = 0;
    while (index < prices.length) {
        if (prices[index].lowerBound <= allocation && prices[index].upperBound >= allocation) {
            return index;
        }
        index++;
    }
    return index;
}

//slider value as percentage
const getInboundAllocationFromSlider = (sliderPercent, prices, minVal, hasOutbound) => {
    const interval = prices.length;
    let minIndex = findIndex(minVal,prices);
    let index = 0;
    let minPercent = minIndex/prices.length +
    (minVal - prices[minIndex].lowerBound)/(prices[minIndex].upperBound-prices[minIndex].lowerBound+1)/prices.length;
    
    let unnormalizedPercent = sliderPercent * (1-minPercent) + minPercent;

    for (let i = 0; i < interval; i++) {
        if (i / interval <= unnormalizedPercent && (i + 1) / interval >= unnormalizedPercent) {
            index = i;
            break;
        }
    }
    const { lowerBound, upperBound } = prices[index];
    
    if (hasOutbound){
        if (sliderPercent < 0.04){ // Special case for clicking 5 free domains
            return 5;
        }
        unnormalizedPercent = ((sliderPercent-0.05)/0.95) * (1-minPercent) + minPercent;
    }
    
    return Math.floor(
        lowerBound + (upperBound - lowerBound) * ((unnormalizedPercent - index / interval) * interval)
        );
};

const getOutboundAllocationFromSlider = (sliderPercent, prices) => {
    const interval = prices.length;
    let index = 0;
    for (let i = 0; i < interval; i++){
        if (i / interval <= sliderPercent && (i + 1) / interval >= sliderPercent){
            index = i;
            break;
        }
    }
    const { lowerBound, upperBound } = prices[index];
    return Math.floor(
        lowerBound + (upperBound - lowerBound) * ((sliderPercent - index / interval) * interval)
    );
};

//get the merged prices ranges
const getAllPricesRange = plans => {
    let mergedPrices = [];
    plans.forEach(plan => {
        mergedPrices = [...mergedPrices, ...getPricesFromPlan(plan)];
    });
    mergedPrices = mergedPrices.sort((a, b) => a.lowerBound > b.lowerBound);

    //set starting point of slider to be 0
    let startingPrice = { lowerBound: 0, pricePerUnit: 0, upperBound: null };
    mergedPrices.unshift(startingPrice);

    //to prevent the case where upperBound is null,
    //set the upperBound to connect to next lowerBound (or max email limits)
    for (let i = 0; i < mergedPrices.length; i++) {
        if (i + 1 < mergedPrices.length) {
            mergedPrices[i].upperBound = mergedPrices[i + 1].lowerBound - 1;
        } else {
            mergedPrices[i].upperBound = mergedPrices[i].upperBound || EMAIL_RANGE_LIMIT;
        }
    }
    return mergedPrices;
};

//returned slider value as percentage
const getInboundSliderFromAllocation = (allocation, prices, minVal, hasOutbound) => {
    let index = findIndex(allocation,prices);
    let minIndex = findIndex(minVal,prices);
    //if couldn't find index of allocation
    if (index === prices.length || minIndex===prices.length){
        if (allocation > prices[prices.length-1].upperBound){
            return 1;
        } else {
            //allocation falls to the left of prices intervals
            return 0;
        }
    }
    const { lowerBound, upperBound } = prices[index];
    //map the (slider) percentage(%)  based on the index and interval count (prices.length)
    let percentage = index/prices.length  + 
        (allocation - lowerBound + 1) / (upperBound - lowerBound + 1)/prices.length;  //adding 1 because both lowerBound and upperBound are inclusive
    // normalize the percentage to be greater than the minimum value
    let minPercent = minIndex/prices.length +
    (minVal - prices[minIndex].lowerBound)/(prices[minIndex].upperBound-prices[minIndex].lowerBound+1)/prices.length;
    let scaledPercentage = (percentage -  minPercent)/ (1 - minPercent);
    if (hasOutbound != null){
        if (allocation < minVal){
            return 0.02;
        }
        scaledPercentage = scaledPercentage * (0.95)+ 0.05 // include space for 5 free domains
    }
    return (
        scaledPercentage
    );
};

const getOutboundSliderFromAllocation = (allocation, prices) => {
    let index = findIndex(allocation, prices);
    if (index === prices.length){
        if (allocation > prices[prices.length-1].upperBound){
            return 1;
        } else {
            return 0;
        }
    }
    const {lowerBound, upperBound} = prices[index];
    return(
        index / prices.length +
        (allocation - lowerBound + 1) / (upperBound - lowerBound + 1) / prices.length //adding 1 because both lowerBound and upperBound are inclusive
    );
};

const emailSliderConverter = (emailCount, plans) => {
    const sliderToEmails = sliderToEmailMap(plans);
    const nearestSlider = sliderToEmails.filter(item => item.count <= emailCount).pop();
    if (nearestSlider.count === emailCount) {
        return nearestSlider.slider;
    }
    const nextPoint = nearestSlider.key + 1;
    return Math.round(
        nearestSlider.slider +
            ((sliderToEmails[nextPoint]["slider"] - nearestSlider["slider"]) /
                (sliderToEmails[nextPoint]["count"] - nearestSlider["count"])) *
                (emailCount - nearestSlider["count"])
    );
};

const estimateCostByEmailCount = (emailCount, plan) => {
    const { basePrice } = plan;
    const extraCost = estimateOverageCostByEmailCount(emailCount, plan);
    const estimateCost = basePrice + extraCost;
    return numeral(estimateCost).value();
};

const estimateOverageCostByEmailCount = (emailCount, plan) => {
    const { overageSupport } = plan;
    if (!overageSupport && emailCount > getEmailLimit(plan)) {
        return Infinity;
    }
    const { features } = plan;
    const feature = features.find(feature => FEATURE_OUTBOUND_EMAILS.includes(feature.handle));
    if (!feature || !feature.pricing) {
        return 0;
    }
    const {
        pricing: { prices, pricingScheme },
        unitName,
    } = feature;
    let extraCost = 0;
    let numericalUnit = numericalUnitsFromProductUnit(unitName);
    switch (pricingScheme) {
        case PRICING_SCHEME_TIERED:
            extraCost = getTierCost(prices, emailCount, numericalUnit);
            break;
        case PRICING_SCHEME_STAIRSTEP:
            extraCost = getStairStepCost(prices, emailCount);
            break;
        default:
            break;
    }
    return extraCost;
};

const numericalUnitsFromProductUnit = unitName => {
    switch (unitName) {
        case THOUSANDS_OF_MESSAGES:
            return 1000;
        default:
            return 1;
    }
};

const getTierCost = (prices, allocation = 0, productUnits = 1) => {
    let total = 0;
    let units = 0;
    for (let { lowerBound, upperBound, pricePerUnit } of prices) {
        if (upperBound !== null && allocation > upperBound) {
            units = (upperBound - lowerBound + 1) / productUnits; //adding 1 because both lowerBound and upperBound are inclusive
            total += pricePerUnit * units;
        } else if (allocation >= lowerBound) {
            //inclusive because if allocation equal to lowerBound will count as 1 unit
            units = (allocation - lowerBound + 1) / productUnits; //adding 1 because both lowerBound and upperBound are inclusive
            total += pricePerUnit * units;
            break;
        }
    }
    //if allocation is higher than last upperbound then just use the largest pricePerUnit available
    if (allocation > prices[prices.length - 1].upperBound) {
        const { upperBound, pricePerUnit } = prices[prices.length - 1];
        units = (allocation - upperBound) / productUnits;
        total += pricePerUnit * units;
    }
    return total;
};

const getStairStepCost = (prices, allocation = 0) => {
    let total = 0;
    for (let { lowerBound, upperBound, pricePerUnit } of prices) {
        if (allocation <= upperBound && allocation >= lowerBound) {
            total = pricePerUnit;
            break;
        }
    }
    return total;
};

const findOutboundByEmailCount = (emailCount, outboundPlans) => {
    let bestOutbound = {};
    let bestPrice = Infinity;
    outboundPlans.forEach(plan => {
        let cost = estimateCostByEmailCount(emailCount, plan);
        if (cost < bestPrice) {
            bestPrice = cost;
            bestOutbound = plan;
        }
    });
    return bestOutbound;
};

const getPlanPrice = (inboundPlan, allocation, outboundHandle = null) => {
    const { basePrice, features } = inboundPlan;

    const feature = features.find(
        feature =>
            FEATURE_PROTECT_DOMAINS.includes(feature.handle) ||
            FEATURE_OUTBOUND_EMAILS.includes(feature.handle)
    );
    if (!feature || !feature.pricing) return null;
    const {
        pricing: { prices, pricingScheme },
    } = feature;

    /**
     * if outbound selected and inbound domains <= 5, inbound will be free
     */
    if (allocation <= 5 && outboundHandle) {
        return 0;
    }
    if (pricingScheme === PRICING_SCHEME_STAIRSTEP) {
        return basePrice + getStairStepCost(prices, allocation);
    }
    if (pricingScheme === PRICING_SCHEME_TIERED) {
        return basePrice + getTierCost(prices, allocation);
    }
};

//given a plan and component allocation, return the price
const getSubTotalPrice = subscription => {
    let total = subscription.price; //basic price
    const { components = [] } = subscription;
    components.forEach(item => {
        total += item.price ? item.price : 0;
    });
    return total;
};

/**
 * return the (outbound) email limit for a outbound plan
 * @param {*} plan
 */
const getEmailLimit = outboundPlan => {
    const feature = outboundPlan.features.find(feature =>
        FEATURE_OUTBOUND_EMAILS.includes(feature.handle)
    );
    if (feature) {
        return getFeatureLimit(feature).limit;
    }
    return {
        limit: null,
        unit: null,
    };
};

// assumes the feature is not null
const getFeatureLimit = (feature, allocation = 0) => {
    const {
        pricing: { prices, pricingScheme },
        productUnit,
    } = feature;
    if (pricingScheme === PRICING_SCHEME_STAIRSTEP) {
        return {
            limit: allocation,
            unit: productUnit,
        };
    }
    if (pricingScheme === PRICING_SCHEME_TIERED) {
        return {
            limit: prices[0].lowerBound,
            unit: productUnit,
        };
    }

    return {
        limit: null,
        unit: null,
    };
};

const getPlanInterval = interval => {
    switch (interval) {
        case "MONTHLY":
            return "mo";

        case "YEARLY":
            return "yr";

        default:
            return "mo";
    }
};

const getPlanOverage = plan => {
    const {
        productComponent: [{ type, productUnit, tiers }],
    } = plan;
    if (type === "TIERED") {
        return {
            price: tiers[0].pricePerUnit,
            unit: productUnit,
        };
    }
    return null;
};

const getPricesFromPlan = plan => {
    const feature = plan.features.find(
        feature =>
            FEATURE_PROTECT_DOMAINS.includes(feature.handle) ||
            FEATURE_OUTBOUND_EMAILS.includes(feature.handle)
    );
    if (!feature || !feature.pricing) {
        return [];
    }
    const { pricing } = feature;
    return pricing.prices;
};

const findPlanByHandle = (handle, plans) => plans.find(plan => plan.planHandle === handle);

const findOutboundIndexByHandle = (handle, plans) => {
    return plans.indexOf(plans.find(plan => plan.planHandle === handle));
};

const findRateLimitedIndices = (allocation, plans) => {
    let indices = [];
    for (let i=0; i < plans.length; i++){
        if (!plans[i].overageSupport && getEmailLimit(plans[i]) < allocation){
            indices.push(i)
        }
    }
    return indices;
}

const friendlyNumber = value => numeral(value).format("0,000,000");
const friendlyPrice = (value, formatStr = "$0,0.00") => {
    if (value===Infinity) {
        return "N/A";
    }
    return numeral(value / 100).format(formatStr); //price are all in cents
};

export {
    sliderEmailConverter,
    emailSliderConverter,
    estimateCostByEmailCount,
    getPlanPrice,
    getSubTotalPrice,
    getPlanOverage,
    getPlanInterval,
    getPricesFromPlan,
    getTierCost,
    getStairStepCost,
    getEmailLimit,
    getAllPricesRange,
    getInboundAllocationFromSlider,
    getOutboundAllocationFromSlider,
    getInboundSliderFromAllocation,
    getOutboundSliderFromAllocation,
    findOutboundByEmailCount,
    findPlanByHandle,
    findOutboundIndexByHandle,
    findRateLimitedIndices,
    friendlyNumber,
    friendlyPrice,
    estimateOverageCostByEmailCount,
    roundToLeft,
    numericalUnitsFromProductUnit,
};
